/**
 * SOLID Review
 * S — Good. The unified email entry point: the form's fields, its mode (sign-in or, by the player's own choice, sign-up), its busy
 *     flag and message, the consent box and the documents it opens, and what a submit asks — the referral field through its own
 *     controller. The checks, the calls and what follows them are two use cases (strict-ports FE3).
 * O — Acceptable. A third identity path (e.g. SSO) would be another mode and another branch of `submit`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Good. The form's state and its actions.
 * D — Strong. The Application's use cases, the request contexts, the referral field's and the consent's controllers.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { ReferralValidation, type IStateReferral } from './ReferralValidation';
import type { ConsentModalController } from './ConsentModalController';
import type { LegalTab } from './LegalModalViewModel';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const BACK_TO_GUEST_CHOICE: TClientUseCaseInput<'navigate'> = { screen: 'GUEST_CHOICE' };

type TLoginMode = 'login' | 'register';

interface IStateLoginOwn {
  readonly mode: TLoginMode;
  readonly email: string;
  readonly displayName: string;
  readonly consentChecked: boolean;
  /** The screen's own busy flag and message (strict-ports FE3: they were the auth store's, shared by every auth screen). */
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly legalOpen: boolean;
  readonly legalInitialTab: LegalTab;
}

/** The form as its view renders it: its own state and the referral field's. */
export interface IStateLogin extends IStateLoginOwn, IStateReferral {}

const BLANK: IStateLoginOwn = {
  mode: 'login', email: '', displayName: '', consentChecked: false, isLoading: false, error: null, legalOpen: false,
  legalInitialTab: 'privacy',
};
const BUSY: Partial<IStateLoginOwn> = { error: null, isLoading: true };
const IDLE: Partial<IStateLoginOwn> = { isLoading: false };
const BACK_TO_SIGN_IN: Partial<IStateLoginOwn> = { mode: 'login', error: null };
const PRIVACY_OPEN: Partial<IStateLoginOwn> = { legalInitialTab: 'privacy', legalOpen: true };
const TERMS_OPEN: Partial<IStateLoginOwn> = { legalInitialTab: 'terms', legalOpen: true };
const LEGAL_CLOSED: Partial<IStateLoginOwn> = { legalOpen: false };

export interface IConfigDepLogin {
  readonly useCases: Pick<IPortClientUseCases, 'requestLoginCode' | 'signUp' | 'navigate'>;
  readonly requests: ClientRequests;
  readonly referral: ReferralValidation;
  readonly consent: ConsentModalController;
}

/**
 * THE EMAIL ENTRY POINT (frontend-framework-oo P3, 2026-10-29, §28.295) — `components/auth/LoginScreen.tsx`'s state and handlers.
 * One email field, two explicit paths (Q10, §28.248): sign-in asks for a login code; "New here? Create an account" turns the same form
 * into sign-up in place — a nickname, a reference code and the legally required consent. The server answers a code request the same
 * for every address, so the form cannot learn from it whether an address has an account; the player says which they are.
 */
export class LoginViewModel implements IServiceViewModel<IStateLogin> {
  private readonly state = new ObservableState<IStateLoginOwn>(BLANK);
  private shown: { readonly own: IStateLoginOwn; readonly field: IStateReferral; readonly form: IStateLogin } | null = null;

  constructor(private readonly deps: IConfigDepLogin) {
    this.manageCookies = deps.consent.openPreferences;
  }

  readonly setEmail = (email: string): void => {
    const typed: Partial<IStateLoginOwn> = { email };
    this.state.update(typed);
  };

  readonly setDisplayName = (displayName: string): void => {
    const typed: Partial<IStateLoginOwn> = { displayName };
    this.state.update(typed);
  };

  readonly setConsentChecked = (consentChecked: boolean): void => {
    const ticked: Partial<IStateLoginOwn> = { consentChecked };
    this.state.update(ticked);
  };

  readonly changeReferral = (typed: string): void => this.deps.referral.change(typed);
  readonly blurReferral = (): Promise<void> => this.deps.referral.blur();

  readonly openPrivacy = (): void => this.state.update(PRIVACY_OPEN);
  readonly openTerms = (): void => this.state.update(TERMS_OPEN);
  readonly closeLegal = (): void => this.state.update(LEGAL_CLOSED);
  /** The legal modal's "Manage cookies": the cookie preferences' own opener. */
  readonly manageCookies: () => void;

  /** The player's own choice between the two paths; the typed address is kept. */
  readonly choose = (mode: TLoginMode): void => {
    const chosen: Partial<IStateLoginOwn> = { mode, error: null };
    this.state.update(chosen);
  };

  /** Back: out of account creation to the plain email prompt, or from the prompt to the guest choice. */
  readonly back = (): void => {
    if (this.state.get().mode === 'register') {
      this.state.update(BACK_TO_SIGN_IN);
      return;
    }
    void this.deps.useCases.navigate(this.deps.requests.context(), BACK_TO_GUEST_CHOICE);
  };

  readonly submit = async (): Promise<void> => {
    const { mode, email, displayName, consentChecked } = this.state.get();
    this.state.update(BUSY);
    try {
      if (mode === 'register') {
        const command: TClientUseCaseInput<'signUp'> = {
          email, displayName, referralCode: ReferralValidation.validatedOf(this.deps.referral.getSnapshot()) ?? null, consentAccepted: consentChecked,
        };
        const signedUp = await this.deps.useCases.signUp(this.deps.requests.context(), command);
        if (!signedUp.success) this.fail(messageFor(signedUp.error, 'Registration failed. Please try again.'));
        return;
      }
      // Sign-in: ask for a login code; the code screen follows, whatever the address is.
      const command: TClientUseCaseInput<'requestLoginCode'> = { email };
      const sent = await this.deps.useCases.requestLoginCode(this.deps.requests.context(), command);
      if (!sent.success) this.fail(messageFor(sent.error, 'Failed to send code. Please try again.'));
    } finally {
      this.state.update(IDLE);
    }
  };

  private fail(error: string): void {
    const failed: Partial<IStateLoginOwn> = { error };
    this.state.update(failed);
  }

  /** The form — the same object until its own state or the referral field's changes. */
  getSnapshot(): IStateLogin {
    const own = this.state.get();
    const field = this.deps.referral.getSnapshot();
    if (this.shown !== null && this.shown.own === own && this.shown.field === field) return this.shown.form;
    const form: IStateLogin = { ...own, referralCode: field.referralCode, referralStatus: field.referralStatus };
    this.shown = { own, field, form };
    return form;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveField = this.deps.referral.subscribe(listener);
    return () => {
      leaveOwn();
      leaveField();
    };
  }

  attach(): void {}

  detach(): void {}
}
